import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { BarChart3 } from 'lucide-react'
import api from '@/lib/axios'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'

interface MonthlyUsage {
  month: string
  documents_processed: number
  pages_processed: number
  extraction_runs: number
  tokens_used: number
  credits_consumed: number
  credits_added: number
}

export default function Usage() {
  const [months, setMonths] = useState(6)

  const { data: usage = [], isLoading } = useQuery({
    queryKey: ['org-usage', months],
    queryFn: () =>
      api.get<MonthlyUsage[]>('/billing/usage', { params: { months } }).then((r) => r.data),
  })

  const totals = usage.reduce(
    (acc, u) => ({
      documents_processed: acc.documents_processed + u.documents_processed,
      pages_processed: acc.pages_processed + (u.pages_processed ?? 0),
      extraction_runs: acc.extraction_runs + u.extraction_runs,
      tokens_used: acc.tokens_used + u.tokens_used,
      credits_consumed: acc.credits_consumed + u.credits_consumed,
      credits_added: acc.credits_added + u.credits_added,
    }),
    { documents_processed: 0, pages_processed: 0, extraction_runs: 0, tokens_used: 0, credits_consumed: 0, credits_added: 0 }
  )

  const currentMonth = new Date().toISOString().slice(0, 7)

  const formatTokens = (t: number) => {
    if (t >= 1_000_000) return `${(t / 1_000_000).toFixed(1)}M`
    if (t >= 1_000) return `${(t / 1_000).toFixed(1)}K`
    return String(t)
  }

  const formatUnits = (n: number) => {
    if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`
    if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`
    return n.toFixed(1)
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Usage</h1>
          <p className="text-muted-foreground">Monthly usage breakdown for your organization</p>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground">Show:</span>
          {[3, 6, 12].map((m) => (
            <button
              key={m}
              onClick={() => setMonths(m)}
              className={`px-3 py-1 text-sm rounded-md border ${
                months === m ? 'bg-primary text-primary-foreground border-primary' : 'hover:bg-muted'
              }`}
            >
              {m} months
            </button>
          ))}
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-4 pb-3 px-4">
            <p className="text-xs text-muted-foreground">Pages Processed</p>
            <p className="text-2xl font-bold">{totals.pages_processed.toLocaleString()}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-3 px-4">
            <p className="text-xs text-muted-foreground">Documents</p>
            <p className="text-2xl font-bold">{totals.documents_processed.toLocaleString()}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-3 px-4">
            <p className="text-xs text-muted-foreground">Tokens Used</p>
            <p className="text-2xl font-bold">{formatTokens(totals.tokens_used)}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-3 px-4">
            <p className="text-xs text-muted-foreground">Credits Consumed</p>
            <p className="text-2xl font-bold">{formatUnits(totals.credits_consumed)}</p>
          </CardContent>
        </Card>
      </div>

      {/* Monthly table */}
      {isLoading ? (
        <div className="text-center py-8 text-muted-foreground">Loading usage data...</div>
      ) : usage.length === 0 ? (
        <div className="text-center py-12 border-2 border-dashed rounded-lg">
          <BarChart3 className="h-8 w-8 text-muted-foreground mx-auto mb-3" />
          <p className="text-muted-foreground">No usage data yet</p>
        </div>
      ) : (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Monthly Breakdown</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <table className="w-full text-sm">
              <thead className="bg-muted/50">
                <tr>
                  <th className="text-left p-3 font-medium">Month</th>
                  <th className="text-right p-3 font-medium">Pages</th>
                  <th className="text-right p-3 font-medium">Documents</th>
                  <th className="text-right p-3 font-medium">Tokens</th>
                  <th className="text-right p-3 font-medium">Credits Consumed</th>
                </tr>
              </thead>
              <tbody>
                {usage.map((u) => (
                  <tr key={u.month} className={`border-t ${u.month === currentMonth ? 'bg-primary/5' : 'hover:bg-muted/20'}`}>
                    <td className="p-3 font-medium">
                      {u.month}
                      {u.month === currentMonth && (
                        <Badge variant="secondary" className="text-[10px] ml-2">Current</Badge>
                      )}
                    </td>
                    <td className="p-3 text-right">{(u.pages_processed ?? 0).toLocaleString()}</td>
                    <td className="p-3 text-right">{u.documents_processed.toLocaleString()}</td>
                    <td className="p-3 text-right text-muted-foreground">{formatTokens(u.tokens_used)}</td>
                    <td className="p-3 text-right">{formatUnits(u.credits_consumed)}</td>
                  </tr>
                ))}
                {/* Totals row */}
                <tr className="border-t-2 bg-muted/30 font-medium">
                  <td className="p-3">Total</td>
                  <td className="p-3 text-right">{totals.pages_processed.toLocaleString()}</td>
                  <td className="p-3 text-right">{totals.documents_processed.toLocaleString()}</td>
                  <td className="p-3 text-right">{formatTokens(totals.tokens_used)}</td>
                  <td className="p-3 text-right">{formatUnits(totals.credits_consumed)}</td>
                </tr>
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}
    </div>
  )
}
